/**
 * Keeping Layouts solved while an editor changes who is inside them.
 *
 * The Layout behavior re-solves when its own data commits, a child's
 * LayoutItem commits, or something invalidates it. It does not follow the
 * hierarchy, so an edit that changes a container's children (a create, a
 * delete, a duplicate, a reparent, an undo that brings one back) would leave
 * it solved for the children it had. The editor listens for the scene's
 * topology changes and, once per burst, invalidates the Layouts around where
 * they happened, or every Layout when a change does not say where; the
 * solve is batched per root and an unchanged scene settles to a no-op.
 */

import type { TraceScene } from '../core/TraceScene';
import type { SceneObject } from '../core/SceneObject';
import { Layout } from '../behaviors/layout';

/** Invalidate every Layout in the scene: after an edit that changed poses or children without a topology event, such as an undo. */
export function settleLayouts(scene: TraceScene): void {
    for (const object of scene.getSceneObjects()) object.getBehavior(Layout)?.invalidate();
}

/**
 * Invalidate the Layouts a hierarchy change at `object` can reach: every one
 * in its subtree, which a move can make a root or put under a new one, and
 * the nearest above it, whose invalidation solves from its root.
 */
function invalidateAround(object: SceneObject): void {
    if (object.transform.isDisposed()) return;
    for (const node of [object, ...object.getDescendants()]) node.getBehavior(Layout)?.invalidate();
    for (let node = object.getParent(); node; node = node.getParent()) {
        const layout = node.getBehavior(Layout);
        if (layout) { layout.invalidate(); return; }
    }
}

/** Settle the scene's Layouts after each burst of topology changes. Returns the detach. */
export function installLayoutTopologySync(scene: TraceScene): () => void {
    let scheduled = false;
    let detached = false;
    // Where the burst changed the hierarchy; null once a change with no place arrives.
    let changedAt: Set<SceneObject> | null = new Set();

    const schedule = (at?: readonly SceneObject[]): void => {
        if (detached) return;
        if (!at) changedAt = null;
        else if (changedAt) for (const object of at) changedAt.add(object);
        if (scheduled) return;
        scheduled = true;
        queueMicrotask(() => {
            scheduled = false;
            const burst = changedAt;
            changedAt = new Set();
            if (detached) return;
            if (!burst) settleLayouts(scene);
            else for (const object of burst) invalidateAround(object);
        });
    };
    const unsubscribe = scene.onTopologyChanged(schedule);
    return () => {
        detached = true;
        unsubscribe();
    };
}
